import { FC, memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, SkeletonLoader, ListSkeleton, Header } from '@robocorp/components';
import { ActivityRunState } from '@robocorp/robocloud-orchestrator-interface';
import { IconEye, IconEyeOff } from '@robocorp/icons/iconic';
import { styled } from '@robocorp/theme';

import {
  useActivityQuery,
  useActivityRunQuery,
  useImagesQuery,
  useWorkerLazyQuery,
} from '~/queries';
import { withErrorBoundary } from '~/hocs';
import { DefinitionList, RouterLink, Timestamp } from '~/components';
import { WorkItemDTO } from '~/types/resources';
import { LOG_ARTIFACT_HTML_FILE_NAME, RuntimeType } from '~/constants';
import { useArtifactLink } from '~/routes/workspace/processes/views/workItem/hooks/useArtifactLink';
import { HideFromMobile } from '~/components/HideFromMobile';
import { useCanReadWorkers } from '~/hooks';
import { LogType } from '.';

const StyledLink = styled(Link)`
  margin-top: -6px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

interface Props {
  organizationShortName: string;
  workspaceShortName: string;
  workItem: WorkItemDTO;
  logType: LogType | false;
  onLogToggle: (type: LogType) => void;
}

export const AdvancedDetails: FC<Props> = ({
  organizationShortName,
  workspaceShortName,
  workItem,
  logType,
  onLogToggle,
}) => {
  const { t } = useTranslation();

  const { processId, processRunId, activityRunId, activityId } = workItem;

  const hasActivityRunId = !!activityRunId;
  const { data: activityRun, isInitialLoading: isActivityRunLoading } = useActivityRunQuery(
    { processId, processRunId, activityRunId: activityRunId as string },
    { enabled: hasActivityRunId },
  );

  const { data: activity, isInitialLoading: isActivityLoading } = useActivityQuery(
    { processId, activityId: activityId as string },
    { enabled: !!processId && !!activityId },
  );

  const { data: logArtifactLink, isLoading: isArtifactLinkLoading } = useArtifactLink(
    processId,
    processRunId,
    activityRunId,
  );

  const canReadWorkers = useCanReadWorkers();

  const [{ data: runtime, isInitialLoading: isRuntimeLoading }, getRuntime] = useWorkerLazyQuery();
  const { data: allImages } = useImagesQuery();

  useEffect(() => {
    if (
      activity?.runtime_type !== RuntimeType.Container &&
      canReadWorkers &&
      activityRun?.runtimeId
    ) {
      getRuntime({ workerId: activityRun.runtimeId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, canReadWorkers]);

  const workerName = useMemo(() => {
    if (activity?.runtime_type === RuntimeType.Container) {
      const image = allImages?.find(({ id }) => id === activity.container_image_id);
      return image?.name || t('workers.cloudWorker');
    }

    if (activityRun?.state === ActivityRunState.AwaitingRuntime) {
      return t(`processRunState.${ActivityRunState.AwaitingRuntime}`);
    }

    if (runtime) {
      return (
        <RouterLink
          to={`/${organizationShortName}/${workspaceShortName}/workers/${runtime.id}`}
          target="_blank"
        >
          {runtime.name ?? '-'}
        </RouterLink>
      );
    }

    return null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, allImages, activityRun, runtime]);

  const robot = activity?.template;
  const isLoading =
    isActivityRunLoading || isActivityLoading || isRuntimeLoading || isArtifactLinkLoading;

  return (
    <Box pt="$12">
      <Header size="small">
        <Header.Title title={t('workItem.additionalInformation')} />
      </Header>
      <SkeletonLoader skeleton={ListSkeleton} loading={isLoading}>
        <DefinitionList my="$12">
          <HideFromMobile breakpoint={3}>
            <DefinitionList.Key>{t('workItem.eventLog.title')}</DefinitionList.Key>
          </HideFromMobile>
          <HideFromMobile breakpoint={3}>
            <DefinitionList.Value>
              <StyledLink
                forwardedAs="button"
                onClick={() => onLogToggle('audit')}
                iconAfter={logType === 'audit' ? IconEyeOff : IconEye}
              >
                {t('workItem.eventLog.show')}
              </StyledLink>
            </DefinitionList.Value>
          </HideFromMobile>

          {!!workItem.exception?.code && (
            <>
              <DefinitionList.Key>{t('exceptionCode')}</DefinitionList.Key>
              <DefinitionList.Value>{workItem.exception?.code}</DefinitionList.Value>
            </>
          )}

          <DefinitionList.Key>{t('process.title')}</DefinitionList.Key>
          <DefinitionList.Value>
            <RouterLink
              to={`/${organizationShortName}/${workspaceShortName}/processes/${processId}/runs/${processRunId}`}
              target="_blank"
            >
              {workItem?.processName ?? '-'}
            </RouterLink>
          </DefinitionList.Value>

          {hasActivityRunId && (
            <>
              <DefinitionList.Key>{t('step')}</DefinitionList.Key>
              <DefinitionList.Value>
                <RouterLink
                  to={`/${organizationShortName}/${workspaceShortName}/processes/${processId}/runs/${processRunId}/stepRuns/${activityRunId}`}
                  target="_blank"
                >
                  {activityRun?.activityName ?? '-'}
                </RouterLink>
              </DefinitionList.Value>
            </>
          )}

          {!!logArtifactLink && (
            <>
              <DefinitionList.Key>{t('workItem.stepLog')}</DefinitionList.Key>
              <DefinitionList.Value>
                <Box display="flex" alignItems="center">
                  <Box mr={8}>
                    <Link href={logArtifactLink} target="_blank">
                      {LOG_ARTIFACT_HTML_FILE_NAME}
                    </Link>
                  </Box>
                  <HideFromMobile breakpoint={3}>
                    <Button
                      size="small"
                      variant="ghost"
                      onClick={() => onLogToggle('file')}
                      iconAfter={logType === 'file' ? IconEyeOff : IconEye}
                      aria-label="toggle log"
                    />
                  </HideFromMobile>
                </Box>
              </DefinitionList.Value>
            </>
          )}

          {!!robot?.id && (
            <>
              <DefinitionList.Key>{t('robot.title')}</DefinitionList.Key>
              <DefinitionList.Value>
                <RouterLink
                  to={`/${organizationShortName}/${workspaceShortName}/robots/${robot.id}`}
                  target="_blank"
                >
                  {robot.name}
                </RouterLink>
              </DefinitionList.Value>
            </>
          )}

          {workerName && (
            <>
              <DefinitionList.Key>{t('workers.singular')}</DefinitionList.Key>
              <DefinitionList.Value>{workerName}</DefinitionList.Value>
            </>
          )}

          {!!workItem?.createTs && (
            <>
              <DefinitionList.Key>{t('created')}</DefinitionList.Key>
              <DefinitionList.Value>
                <Timestamp value={workItem?.createTs} year />
              </DefinitionList.Value>
            </>
          )}

          {!!workItem?.stateTs && (
            <>
              <DefinitionList.Key>{t('updated')}</DefinitionList.Key>
              <DefinitionList.Value>
                <Timestamp value={workItem?.stateTs} year />
              </DefinitionList.Value>
            </>
          )}
        </DefinitionList>
      </SkeletonLoader>
    </Box>
  );
};

export const WorkItemAdvancedDetails = withErrorBoundary(memo(AdvancedDetails));
